import { DEFAULT_USER_DATA } from "@/constants/defaults"
import type { TLoginUserData } from "@/schemas/user.schema"
import { createStore, useStore } from "zustand"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { setCookie } from "cookies-next/client"
import { persist } from "zustand/middleware"
import { createContext, useContext, useRef } from "react"
import { appStore$ } from "@/stores/app.v2.store"
import { getPersistedAppStoreState } from "@/stores/app-store-persistence"

interface TWebphoneDetachedPosition {
  x: number
  y: number
  width: number
  height: number
  screenX: number
  screenY: number
}

interface TAppStore {
  isSidebarMenuExpanded: boolean
  userData: TLoginUserData
  _old_webphone: boolean
  webphoneSelectedContactID?: string
  webphoneExpanded: boolean
  webphonePoppedOut: boolean
  webphonePopup: {
    height: number
    width: number
  }
  webphoneDetached: boolean
  webphoneDetachedPosition: TWebphoneDetachedPosition
}

interface TAppStoreWithFunctions extends TAppStore {
  changeSidebarMenuState: (setExpanded: boolean) => void
  setUserData: (userData: TLoginUserData) => void
  setWebphoneExpanded: (expanded: boolean) => void
  setWebphonePoppedOut: (poppedOut: boolean) => void
  setWebphonePopupSize: ({ width, height }: { width: number; height: number }) => void
  changeWebphonePopupOld: (old: boolean) => void
  changeSelectedContactID: (contactID?: string) => void
  setWebphoneDetached: (detached: boolean) => void
  setWebphoneDetachedPosition: (position: TWebphoneDetachedPosition) => void
}

const DEFAULT_DETACHED_POSITION: TWebphoneDetachedPosition = {
  x: 100,
  y: 100,
  width: 420,
  height: 700,
  screenX: 100,
  screenY: 100,
}

const initialState: TAppStore = {
  isSidebarMenuExpanded: false,
  userData: {
    ...DEFAULT_USER_DATA,
  },
  _old_webphone: false,
  webphoneExpanded: false,
  webphonePoppedOut: false,
  webphonePopup: {
    height: 600,
    width: 420,
  },
  webphoneDetached: false,
  webphoneDetachedPosition: DEFAULT_DETACHED_POSITION,
}

export const createAppStore = (initProps?: Partial<TAppStore>) => {
  const definedInitProps = getDefinedAppStoreProps(initProps ?? {})

  return createStore<TAppStoreWithFunctions>()(
    persist(
      (set) => ({
        ...initialState,
        ...definedInitProps,
        webphoneSelectedContactID: undefined,
        webphoneExpanded: false,
        webphonePoppedOut: false,
        webphoneDetached: false,
        changeSidebarMenuState: (setExpanded) => {
          set(() => ({ isSidebarMenuExpanded: setExpanded }))
          appStore$.isSidebarMenuExpanded.set(setExpanded)
        },
        setUserData: (userData) => {
          set(() => ({ userData }))
          appStore$.userData.set(userData)
        },
        setWebphoneExpanded: (_expanded) => {
          // @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns opening and closing the webphone.
          // setCookie("webphoneExpanded", expanded, {
          //   httpOnly: false,
          // })
          // set(() => ({ webphoneExpanded: expanded }))
          // appStore$.webphoneExpanded.set(expanded)
        },
        setWebphonePoppedOut: (_poppedOut) => {
          // @deprecated The embedded frontend webphone is disabled. Popping out the frontend webphone is a no-op.
          // set(() => ({ webphonePoppedOut: poppedOut }))
          // appStore$.webphonePoppedOut.set(poppedOut)
        },
        setWebphonePopupSize: (_size) => {
          // @deprecated The embedded frontend webphone is disabled. Popup sizing is now owned by the Chrome extension sidebar.
          // set(() => ({ webphonePopup: { width, height } }))
          // appStore$.webphonePopup.set({ width, height })
        },
        changeWebphonePopupOld: (_old) => {
          // @deprecated The embedded frontend webphone is disabled. Switching old/new frontend webphone modes is a no-op.
          // set(() => ({
          //   _old_webphone: old,
          // }))
          // appStore$._old_webphone.set(old)
        },
        changeSelectedContactID: (_contactID) => {
          // @deprecated The embedded frontend webphone is disabled. Selecting a contact in the frontend webphone is a no-op.
          // set(() => ({ webphoneSelectedContactID: contactID }))
          // appStore$.webphoneSelectedContactID.set(contactID)
        },
        setWebphoneDetached: (_detached) => {
          // @deprecated The embedded frontend webphone is disabled. Detach/reattach is a no-op.
          // set(() => ({ webphoneDetached: detached }))
          // appStore$.webphoneDetached.set(detached)
        },
        setWebphoneDetachedPosition: (_position) => {
          // @deprecated The embedded frontend webphone is disabled. Detached window position is no longer persisted.
          // set(() => ({ webphoneDetachedPosition: position }))
          // appStore$.webphoneDetachedPosition.set(position)
          // try {
          //   localStorage.setItem("webphoneDetachedPosition", JSON.stringify(position))
          // } catch {
          //   // Ignore localStorage errors
          // }
        },
      }),
      {
        name: "appstore-channel",
        partialize: (state) => {
          return getPersistedAppStoreState(state)
        },
      }
    )
  )
}

export const AppStoreContext = createContext<ReturnType<typeof createAppStore> | null>(null)

type AppStoreProviderProps = React.PropsWithChildren<Partial<TAppStore>>

function getDefinedAppStoreProps(props: Partial<TAppStore>) {
  return Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined)) as Partial<
    TAppStore
  >
}

export function AppStoreProvider({ children, ...props }: AppStoreProviderProps) {
  const storeRef = useRef<ReturnType<typeof createAppStore> | null>(null)
  if (!storeRef.current) {
    storeRef.current = createAppStore(getDefinedAppStoreProps(props))
  }
  return <AppStoreContext.Provider value={storeRef.current}>{children}</AppStoreContext.Provider>
}

export function useAppStoreContext<T>(selector: (state: TAppStoreWithFunctions) => T): T {
  const store = useContext(AppStoreContext)
  if (!store) throw new Error("Missing AppStoreContext.Provider in the tree")
  return useStore(store, selector)
}
